import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import {
    ParameterService,
    CreateParameterRequest,
} from '../../../services/parameter.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';

@Component({
    selector: 'app-create-parameter-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
    ],
    templateUrl: './create-parameter-page.component.html',
    styleUrl: './create-parameter-page.component.scss',
})
export class CreateParameterPageComponent implements OnInit {
    parameterForm: FormGroup;
    isLoading = false;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.parameterForm = this.fb.group({
            key: [
                '',
                [
                    Validators.required,
                    Validators.maxLength(100),
                    Validators.pattern(/^[A-Z][A-Z0-9_]*$/),
                ],
            ],
            value: ['', [Validators.required, Validators.maxLength(500)]],
            description: ['', [Validators.maxLength(500)]],
            isActive: [true],
        });
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        // Form is already initialized in constructor
    }

    getErrorMessage(fieldName: string): string {
        const control = this.parameterForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('maxlength')) {
            const requiredLength =
                control.errors?.['maxlength']?.requiredLength;
            return `Máximo ${requiredLength} caracteres`;
        }

        if (control.hasError('pattern')) {
            return 'La clave debe estar en formato SCREAMING_SNAKE_CASE (ej: MAX_LOGIN_ATTEMPTS)';
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.parameterForm.controls).forEach((key) => {
            const control = this.parameterForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        if (this.parameterForm.valid) {
            this.isLoading = true;

            const formData = this.parameterForm.value;
            const parameterData: CreateParameterRequest = {
                key: formData.key.trim().toUpperCase(),
                value: formData.value.trim(),
                description: formData.description?.trim() || undefined,
                isActive:
                    formData.isActive !== undefined ? formData.isActive : true,
            };

            this.parameterService
                .createParameter(parameterData)
                .pipe(
                    catchError((error) => {
                        this.logger.error(
                            'Error al crear parámetro',
                            error as Error,
                            'CreateParameterPageComponent'
                        );
                        const errorMessage =
                            error.error?.message ||
                            'Error al crear el parámetro';
                        this.snackBar.open(errorMessage, 'Cerrar', {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        });
                        return of(null);
                    }),
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe((response) => {
                    if (response && response.success) {
                        this.snackBar.open(
                            'Parámetro creado exitosamente',
                            'Cerrar',
                            {
                                duration: 3000,
                                panelClass: ['success-snackbar'],
                            }
                        );
                        this.router.navigate(['/parameters']);
                    }
                });
        } else {
            this.markFormGroupTouched();
        }
    }

    onCancel(): void {
        this.router.navigate(['/parameters']);
    }
}
